/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keys } from '../../services';
export var EuiTableRow = ({
  children,
  className,
  isSelected,
  isSelectable,
  hasActions,
  isExpandedRow,
  isExpandable,
  onClick,
  ...rest
}) => {
  const classes = classNames('euiTableRow', className, {
    'euiTableRow-isSelectable': isSelectable,
    'euiTableRow-isSelected': isSelected,
    'euiTableRow-hasActions': hasActions,
    'euiTableRow-isExpandedRow': isExpandedRow,
    'euiTableRow-isExpandable': isExpandable,
    'euiTableRow-isClickable': onClick
  });
  if (!onClick) {
    return <tr className={classes} {...rest}>
        {children}
      </tr>;
  }
  const onKeyDown = event => {
    // Prevent a scroll from occurring if the user has hit space.
    if (event.key === keys.SPACE) event.preventDefault();
  };
  const onKeyUp = event => {
    // Support keyboard accessibility by emulating mouse click on ENTER or SPACE keypress.
    if (event.key === keys.ENTER || event.key === keys.SPACE) {
      onClick(event);
    }
  };
  return <tr className={classes} onClick={onClick} onKeyDown={onKeyDown} onKeyUp={onKeyUp} tabIndex={0} {...rest}>
      {children}
    </tr>;
};
EuiTableRow.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Indicates if the table has a single column of checkboxes for selecting
     * rows (affects mobile only)
     */
  isSelectable: PropTypes.bool,
  /**
     * Indicates the current row has been selected
     */
  isSelected: PropTypes.bool,
  /**
     * Indicates if the table has a dedicated column for icon-only actions
     * (affects mobile only)
     */
  hasActions: PropTypes.bool,
  /**
     * Indicates if the row will have an expanded row
     */
  isExpandable: PropTypes.bool,
  /**
     * Indicates if the row will be the expanded row
     */
  isExpandedRow: PropTypes.bool,
  onClick: PropTypes.any
};
try {
  EuiTableRow.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/table/table_row.tsx',
    description: '',
    displayName: 'EuiTableRow',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      isSelectable: {
        defaultValue: null,
        description: 'Indicates if the table has a single column of checkboxes for selecting\n' + 'rows (affects mobile only)',
        name: 'isSelectable',
        parent: {
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Indicates the current row has been selected',
        name: 'isSelected',
        parent: {
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasActions: {
        defaultValue: null,
        description: 'Indicates if the table has a dedicated column for icon-only actions\n' + '(affects mobile only)',
        name: 'hasActions',
        parent: {
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isExpandable: {
        defaultValue: null,
        description: 'Indicates if the row will have an expanded row',
        name: 'isExpandable',
        parent: {
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isExpandedRow: {
        defaultValue: null,
        description: 'Indicates if the row will be the expanded row',
        name: 'isExpandedRow',
        parent: {
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row.tsx',
          name: 'EuiTableRowProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiTableRowProps']
  };
} catch (e) {}